// Progress storage adapter — the boilerplate every integrator used to write
// by hand, now built in: account-bound storage when the SDK is present, a
// localStorage fallback otherwise, and a one-shot migration of a legacy
// localStorage save into the account on first SDK launch.

import type { ProgressData } from './types';

export interface StorageDeps {
  /** Awaits host resolution (real or mock). Returns true if in-app. */
  ensureReady: () => Promise<boolean>;
  /** Raw account-bound load. Returns the saved object or null. */
  loadProgress: () => Promise<ProgressData | null>;
  /** Raw account-bound save. Returns true on success. */
  saveProgress: (data: ProgressData) => Promise<boolean>;
  /**
   * Resolves the current localStorage key for the fallback + legacy
   * migration. Read lazily (not captured once) so a `configure({ storageKey })`
   * call made after the SDK singleton is constructed still takes effect.
   */
  getStorageKey: () => string;
}

export interface GammaStorage {
  /** Resolve the host once. Call before {@link load}. Safe to call repeatedly. */
  init(): Promise<boolean>;
  /** True once the host (real or mock) is resolved. */
  ready(): boolean;
  /** Load progress: account-bound when available, else localStorage. */
  load(): Promise<ProgressData | null>;
  /** Save progress: account-bound when available, else localStorage. */
  save(state: ProgressData): Promise<boolean>;
}

export function createStorage(deps: StorageDeps): GammaStorage {
  let resolved = false;
  let inApp = false;

  function readLocal(): ProgressData | null {
    try {
      const raw = localStorage.getItem(deps.getStorageKey());
      return raw ? (JSON.parse(raw) as ProgressData) : null;
    } catch {
      return null;
    }
  }

  function writeLocal(state: ProgressData): boolean {
    try {
      localStorage.setItem(deps.getStorageKey(), JSON.stringify(state));
      return true;
    } catch {
      return false;
    }
  }

  return {
    async init() {
      inApp = await deps.ensureReady();
      resolved = true;
      return inApp;
    },

    ready() {
      return resolved;
    },

    async load() {
      if (!resolved) inApp = await deps.ensureReady().then((v) => ((resolved = true), v));

      if (!inApp) return readLocal();

      const remote = await deps.loadProgress();
      if (remote !== null) return remote;

      // First SDK launch with no account save: migrate a legacy local save.
      const legacy = readLocal();
      if (legacy) {
        const ok = await deps.saveProgress(legacy);
        if (ok) {
          try {
            localStorage.removeItem(deps.getStorageKey());
          } catch {
            /* ignore */
          }
        }
        return legacy;
      }
      return null;
    },

    async save(state) {
      if (!resolved) inApp = await deps.ensureReady().then((v) => ((resolved = true), v));
      if (inApp) return deps.saveProgress(state);
      return writeLocal(state);
    },
  };
}
